import { useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useCreateCategory } from '../../hooks/create-category.hook';
import { CATEGORY_NAME_MAX_LENGTH, CATEGORY_NAME_MIN_LENGTH } from '../../constants/category-name-length.constants';

type Props = {
  onCategoryCreated: () => void;
};

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  checkbox: {
    '&.Mui-checked': {
      color: theme.palette.admin.primary.main,
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  createButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function CreateCategoryForm({ onCategoryCreated }: Props) {
  const classes = useStyles();
  const { createCategory, isSubmitting } = useCreateCategory();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  const isFormDirty = name !== '' || description !== '' || !isActive;
  const isValid = trimmedName.length >= CATEGORY_NAME_MIN_LENGTH && trimmedDescription !== '';
  const isSubmitDisabled = isSubmitting || !isValid;

  const handleReset = () => {
    setName('');
    setDescription('');
    setIsActive(true);
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    const result = await createCategory({
      name: trimmedName,
      description: trimmedDescription,
      isActive,
    });

    if (result) {
      handleReset();
      onCategoryCreated();
    }
  };

  return (
    <div className={classes.form}>
      <TextField
        id="create-category-name"
        label="Nom"
        type="text"
        inputProps={{ maxLength: CATEGORY_NAME_MAX_LENGTH }}
        value={name}
        size="small"
        variant="outlined"
        fullWidth
        required
        onChange={(event) => setName(event.target.value)}
      />
      <TextField
        id="create-category-description"
        label="Description"
        type="text"
        value={description}
        size="small"
        variant="outlined"
        fullWidth
        required
        multiline
        minRows={3}
        onChange={(event) => setDescription(event.target.value)}
      />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            color="default"
            className={classes.checkbox}
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
        }
        label="Actif"
      />
      <div className={classes.actions}>
        <Button size="small" variant="outlined" onClick={handleReset} disabled={!isFormDirty || isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          Créer
        </Button>
      </div>
    </div>
  );
}
